import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, ArrowLeft, Calendar, MapPin, Camera, Dog,
  Users, Search, Globe, Grid3X3, ShoppingCart, X,
  Plus, Minus, Trash2, Package, CreditCard, Check,
} from 'lucide-react';
import axios from 'axios';
import SportGalleryToolbar from '../components/sport/SportGalleryToolbar';
import SportPhotoGrid from '../components/sport/SportPhotoGrid';
import SportLightbox from '../components/sport/SportLightbox';
import useFavorites from '../hooks/useFavorites';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persistent cart ID (per browser)
function getCartId() {
  let id = localStorage.getItem('sport_cart_id');
  if (!id) {
    id = 'cart_' + Math.random().toString(36).slice(2, 14);
    localStorage.setItem('sport_cart_id', id);
  }
  return id;
}

export default function SportEventPage() {
  const { eventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language, setLanguage } = useLanguage();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState(searchParams.get('tab') || 'photos');
  const [filterDogId, setFilterDogId] = useState(searchParams.get('dog') || '');

  // Gallery state
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterRatings, setFilterRatings] = useState([]);
  const [filenameSearch, setFilenameSearch] = useState('');
  const [filterTeamId, setFilterTeamId] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [filterPhotographerId, setFilterPhotographerId] = useState('');
  const [filterCompetitorId, setFilterCompetitorId] = useState('');
  const [filterCamera, setFilterCamera] = useState('');
  const [filterBurstType, setFilterBurstType] = useState('');
  const [filterTimeStart, setFilterTimeStart] = useState('');
  const [filterTimeEnd, setFilterTimeEnd] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Filter data
  const [competitors, setCompetitors] = useState([]);
  const [cameras, setCameras] = useState([]);

  // Favorites
  const { favoriteIds, toggleFavorite } = useFavorites();

  // Cart state
  const cartId = getCartId();
  const [cart, setCart] = useState({ items: [], total: 0, items_count: 0 });
  const [cartOpen, setCartOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [addingToCart, setAddingToCart] = useState({});
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');

  const t = language === 'pl' ? {
    all: 'Wszystkie', photos: 'zdjec', dogs: 'psow', teams: 'druzyn',
    findDog: 'Znajdz psa', notFound: 'Zawody nie znalezione',
    noPhotos: 'Brak zdjec', prev: 'Poprzednia', next: 'Nastepna',
    addToCart: 'Do koszyka', inCart: 'W koszyku', cart: 'Koszyk',
    dogPack: 'Pakiet', allPhotos: 'wszystkie zdjecia', discount: 'znizka',
    emptyCart: 'Koszyk jest pusty', total: 'Suma', checkout: 'Zamow',
    clearCart: 'Wyczysc', perPhoto: 'za zdjecie', packPrice: 'cena pakietu',
    buyerInfo: 'Dane kupujacego', name: 'Imie i nazwisko', email: 'Email',
    phone: 'Telefon', pay: 'Przejdz do platnosci', ordering: 'Przetwarzanie...',
    close: 'Zamknij', remove: 'Usun', items: 'szt.',
  } : {
    all: 'All', photos: 'photos', dogs: 'dogs', teams: 'teams',
    findDog: 'Find dog', notFound: 'Event not found',
    noPhotos: 'No photos', prev: 'Previous', next: 'Next',
    addToCart: 'Add to cart', inCart: 'In cart', cart: 'Cart',
    dogPack: 'Pack', allPhotos: 'all photos', discount: 'discount',
    emptyCart: 'Cart is empty', total: 'Total', checkout: 'Checkout',
    clearCart: 'Clear', perPhoto: 'per photo', packPrice: 'pack price',
    buyerInfo: 'Buyer info', name: 'Full name', email: 'Email',
    phone: 'Phone', pay: 'Proceed to payment', ordering: 'Processing...',
    close: 'Close', remove: 'Remove', items: 'items',
  };

  // Fetch event
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${API}/sport/public/events/${eventId}`);
        setEvent(res.data);
      } catch (err) {
        console.error('Failed to load event:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId]);

  // Fetch pricing
  useEffect(() => {
    axios.get(`${API}/sport/shop/pricing/${eventId}`)
      .then(res => setPricing(res.data))
      .catch(() => setPricing({ per_photo_price: 29, dog_pack_discount: 20, currency: 'PLN' }));
  }, [eventId]);

  // Fetch photos
  const fetchPhotos = async (silent = false) => {
    if (!event) return;
    if (!silent) setPhotosLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: pageSize, sort: sortBy });
      if (filterDogId) params.set('dog_id', filterDogId);
      if (filterTeamId) params.set('team_id', filterTeamId);
      if (filterRatings.length > 0) params.set('ratings', filterRatings.join(','));
      if (filenameSearch) params.set('filename', filenameSearch);
      if (filterPhotographerId) params.set('photographer_id', filterPhotographerId);
      if (filterCompetitorId) params.set('competitor_id', filterCompetitorId);
      if (dateStart) params.set('date_start', dateStart);
      if (dateEnd) params.set('date_end', dateEnd);
      if (filterCamera) params.set('camera', filterCamera);
      if (filterBurstType) params.set('burst_filter', filterBurstType);
      if (filterTimeStart) params.set('time_start', filterTimeStart);
      if (filterTimeEnd) params.set('time_end', filterTimeEnd);
      const res = await axios.get(`${API}/sport/public/events/${eventId}/photos?${params}`);
      setPhotos(res.data.photos);
      setTotal(res.data.total);
    } catch (err) {
      if (!silent) console.error('Failed to load photos:', err);
    } finally {
      if (!silent) setPhotosLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [event, eventId, page, pageSize, filterDogId, filterTeamId, filterPhotographerId, filterCompetitorId, sortBy, filterRatings, filenameSearch, dateStart, dateEnd, filterCamera, filterBurstType, filterTimeStart, filterTimeEnd]);

  // Auto-refresh gallery every 15s to pick up new imports from FS
  useEffect(() => {
    if (!event) return;
    const interval = setInterval(() => fetchPhotos(true), 15000);
    return () => clearInterval(interval);
  }, [event, eventId, page, pageSize, filterDogId, filterTeamId, filterPhotographerId, filterCompetitorId, sortBy, filterRatings, filenameSearch, dateStart, dateEnd, filterCamera, filterBurstType, filterTimeStart, filterTimeEnd]);

  // Fetch dogs and competitors
  useEffect(() => {
    if (!event) return;
    axios.get(`${API}/sport/public/events/${eventId}/dogs`)
      .then(res => setDogs(res.data.dogs))
      .catch(err => console.error('Failed to load dogs:', err));
    axios.get(`${API}/sport/public/events/${eventId}/competitors`)
      .then(res => setCompetitors(res.data.competitors || []))
      .catch(err => console.error('Failed to load competitors:', err));
    axios.get(`${API}/sport/public/events/${eventId}/cameras`)
      .then(res => setCameras(res.data.cameras || []))
      .catch(err => console.error('Failed to load cameras:', err));
  }, [event, eventId]);

  // Fetch cart
  const refreshCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sport/shop/cart/${cartId}`);
      setCart(res.data);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }, [cartId]);

  useEffect(() => { refreshCart(); }, [refreshCart]);

  const cartPhotoIds = new Set(cart.items.map(i => i.photo_id));

  const handleFilterDog = (dogId) => {
    setFilterDogId(dogId === filterDogId ? '' : dogId);
    setPage(1);
    setSearchParams(dogId ? { tab, dog: dogId } : { tab });
  };

  const handleAddToCart = async (photoId) => {
    setAddingToCart(prev => ({ ...prev, [photoId]: true }));
    try {
      await axios.post(`${API}/sport/shop/cart/${cartId}/add`, {
        event_id: eventId, photo_id: photoId,
      });
      await refreshCart();
    } catch (err) {
      console.error('Add to cart error:', err);
    } finally {
      setAddingToCart(prev => ({ ...prev, [photoId]: false }));
    }
  };

  const handleAddDogPack = async (dogId) => {
    setAddingToCart(prev => ({ ...prev, [`pack_${dogId}`]: true }));
    try {
      await axios.post(`${API}/sport/shop/cart/${cartId}/add-dog-pack`, {
        event_id: eventId, dog_id: dogId,
      });
      await refreshCart();
    } catch (err) {
      console.error('Add dog pack error:', err);
    } finally {
      setAddingToCart(prev => ({ ...prev, [`pack_${dogId}`]: false }));
    }
  };

  const handleRemoveFromCart = async (photoId) => {
    try {
      await axios.delete(`${API}/sport/shop/cart/${cartId}/item/${photoId}`);
      await refreshCart();
    } catch (err) {
      console.error('Remove from cart error:', err);
    }
  };

  const handleClearCart = async () => {
    try {
      await axios.delete(`${API}/sport/shop/cart/${cartId}`);
      await refreshCart();
    } catch (err) {
      console.error('Clear cart error:', err);
    }
  };

  const handleCheckout = async () => {
    if (!buyerName || !buyerEmail) return;
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/sport/shop/cart/${cartId}/checkout`, {
        buyer_name: buyerName,
        buyer_email: buyerEmail,
        buyer_phone: buyerPhone,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">{t.notFound}</p>
      </div>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {event.season_id && (
              <Link to={`/sport/season/${event.season_id}`} className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            )}
            <Dog className="w-6 h-6" />
            <div>
              <h1 className="text-lg font-bold">{event.name}</h1>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {event.location && <><MapPin className="w-3 h-3" /> {event.location}</>}
                {event.date_start && (
                  <><Calendar className="w-3 h-3 ml-2" /> {new Date(event.date_start).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US')}</>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Cart button */}
            <Button
              variant={cart.items_count > 0 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCartOpen(true)}
              className="relative"
            >
              <ShoppingCart className="w-4 h-4" />
              {cart.items_count > 0 && (
                <span className="ml-1 text-xs font-bold">{cart.items_count}</span>
              )}
            </Button>
            <Link to={`/sport/event/${eventId}/find`}>
              <Button variant="outline" size="sm">
                <Search className="w-4 h-4 mr-1" />
                {t.findDog}
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
            >
              <Globe className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Stats bar with pricing */}
      <section className="border-b border-border bg-secondary/30 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-6 text-sm">
          <span className="flex items-center gap-1">
            <Camera className="w-4 h-4" /> {event.photo_count} {t.photos}
          </span>
          <span className="flex items-center gap-1">
            <Dog className="w-4 h-4" /> {event.dog_count} {t.dogs}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" /> {event.team_count} {t.teams}
          </span>
          {pricing && (
            <span className="ml-auto flex items-center gap-1 text-primary font-medium">
              <CreditCard className="w-4 h-4" />
              {pricing.per_photo_price} {pricing.currency} {t.perPhoto}
            </span>
          )}
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-border px-6">
        <div className="max-w-7xl mx-auto flex gap-1">
          {[
            { key: 'photos', icon: Grid3X3, pl: 'Zdjecia', en: 'Photos' },
            { key: 'dogs', icon: Dog, pl: 'Psy', en: 'Dogs' },
            { key: 'teams', icon: Users, pl: 'Druzyny', en: 'Teams' },
          ].map(tb => (
            <button
              key={tb.key}
              onClick={() => { setTab(tb.key); setSearchParams({ tab: tb.key }); }}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === tb.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tb.icon className="w-4 h-4" />
              {language === 'pl' ? tb.pl : tb.en}
            </button>
          ))}
        </div>
      </section>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Photos Tab */}
        {tab === 'photos' && (
          <div>
            {/* Toolbar with all filters */}
            <SportGalleryToolbar
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              sortBy={sortBy}
              onSortChange={(v) => { setSortBy(v); setPage(1); }}
              filterRatings={filterRatings}
              onRatingsChange={(v) => { setFilterRatings(v); setPage(1); }}
              filenameSearch={filenameSearch}
              onFilenameSearchChange={(v) => { setFilenameSearch(v); setPage(1); }}
              filterDogId={filterDogId}
              onDogIdChange={(v) => { handleFilterDog(v); }}
              filterTeamId={filterTeamId}
              onTeamIdChange={(v) => { setFilterTeamId(v); setPage(1); }}
              dateStart={dateStart}
              onDateStartChange={(v) => { setDateStart(v); setPage(1); }}
              dateEnd={dateEnd}
              onDateEndChange={(v) => { setDateEnd(v); setPage(1); }}
              pageSize={pageSize}
              onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
              photographers={event?.photographers || []}
              filterPhotographerId={filterPhotographerId}
              onPhotographerIdChange={(v) => { setFilterPhotographerId(v); setPage(1); }}
              competitors={competitors}
              filterCompetitorId={filterCompetitorId}
              onCompetitorIdChange={(v) => { setFilterCompetitorId(v); setPage(1); }}
              cameras={cameras}
              filterCamera={filterCamera}
              onCameraChange={(v) => { setFilterCamera(v); setPage(1); }}
              filterBurstType={filterBurstType}
              onBurstTypeChange={(v) => { setFilterBurstType(v); setPage(1); }}
              filterTimeStart={filterTimeStart}
              onTimeStartChange={(v) => { setFilterTimeStart(v); setPage(1); }}
              filterTimeEnd={filterTimeEnd}
              onTimeEndChange={(v) => { setFilterTimeEnd(v); setPage(1); }}
              total={total}
              dogs={dogs}
              teams={event?.teams || []}
              language={language}
            />

            {photosLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : photos.length === 0 ? (
              <div className="text-center py-20">
                <Camera className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">{t.noPhotos}</p>
              </div>
            ) : (
              <>
                {/* Top pagination (compact) */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mb-4">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      {t.prev}
                    </Button>
                    <span className="flex items-center text-sm text-muted-foreground px-3">
                      {page} / {totalPages}
                    </span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      {t.next}
                    </Button>
                  </div>
                )}

                <SportPhotoGrid
                  photos={photos}
                  viewMode={viewMode}
                  cartPhotoIds={cartPhotoIds}
                  addingToCart={addingToCart}
                  favoriteIds={favoriteIds}
                  onToggleFavorite={toggleFavorite}
                  pricing={pricing}
                  onAddToCart={handleAddToCart}
                  onPhotoClick={(idx) => { setCurrentPhotoIndex(idx); setLightboxOpen(true); }}
                  t={t}
                />

                {/* Bottom pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      {t.prev}
                    </Button>
                    <span className="flex items-center text-sm text-muted-foreground px-3">
                      {page} / {totalPages}
                    </span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      {t.next}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Dogs Tab */}
        {tab === 'dogs' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {dogs.map((dog, idx) => (
              <motion.div
                key={dog.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
              >
                <Card className="hover:border-primary/30 transition-all group">
                  <CardContent className="p-4">
                    <Link to={`/sport/dog/${dog.id}`} className="text-center block mb-3">
                      <Dog className="w-10 h-10 mx-auto mb-2 text-muted-foreground group-hover:text-primary transition-colors" />
                      <h4 className="font-medium">{dog.name || `#${dog.id.slice(0, 6)}`}</h4>
                      {dog.breed && <p className="text-xs text-muted-foreground">{dog.breed}</p>}
                      {dog.team_name && <p className="text-xs text-muted-foreground mt-1">{dog.team_name}</p>}
                      <p className="text-xs text-muted-foreground mt-2">
                        {dog.event_photo_count} {t.photos}
                      </p>
                    </Link>
                    {/* Dog pack button */}
                    {dog.event_photo_count > 0 && pricing && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => handleAddDogPack(dog.id)}
                        disabled={addingToCart[`pack_${dog.id}`]}
                      >
                        {addingToCart[`pack_${dog.id}`] ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Package className="w-3 h-3 mr-1" />
                        )}
                        {t.dogPack} — {dog.event_photo_count} {t.photos}
                        {pricing.dog_pack_discount > 0 && (
                          <span className="ml-1 text-green-500">-{pricing.dog_pack_discount}%</span>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Teams Tab */}
        {tab === 'teams' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(event.teams || []).map((team, idx) => (
              <motion.div
                key={team.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link to={`/sport/team/${team.id}`}>
                  <Card className="hover:border-primary/30 transition-all cursor-pointer group">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <Users className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors" />
                        <div>
                          <h4 className="font-semibold">{team.name}</h4>
                          {team.club && <p className="text-sm text-muted-foreground">{team.club}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && photos.length > 0 && (
          <SportLightbox
            photos={photos}
            currentIndex={currentPhotoIndex}
            onIndexChange={setCurrentPhotoIndex}
            onClose={() => setLightboxOpen(false)}
            cartPhotoIds={cartPhotoIds}
            addingToCart={addingToCart}
            favoriteIds={favoriteIds}
            onToggleFavorite={toggleFavorite}
            pricing={pricing}
            onAddToCart={handleAddToCart}
            language={language}
            t={t}
          />
        )}
      </AnimatePresence>

      {/* Cart Drawer */}
      <AnimatePresence>
        {cartOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-50"
              onClick={() => { setCartOpen(false); setCheckoutOpen(false); }}
            />
            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-background border-l border-border z-50 flex flex-col"
            >
              {/* Cart header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  <h2 className="text-lg font-bold">{t.cart}</h2>
                  <span className="text-sm text-muted-foreground">({cart.items_count} {t.items})</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setCartOpen(false); setCheckoutOpen(false); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Cart items */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {cart.items.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground">{t.emptyCart}</p>
                  </div>
                ) : checkoutOpen ? (
                  /* Checkout form */
                  <div className="space-y-4">
                    <h3 className="font-semibold">{t.buyerInfo}</h3>
                    <div>
                      <label className="text-sm text-muted-foreground">{t.name} *</label>
                      <input
                        type="text"
                        value={buyerName}
                        onChange={e => setBuyerName(e.target.value)}
                        className="w-full mt-1 px-3 py-2 bg-secondary border border-border rounded-md text-sm"
                        placeholder="Jan Kowalski"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">{t.email} *</label>
                      <input
                        type="email"
                        value={buyerEmail}
                        onChange={e => setBuyerEmail(e.target.value)}
                        className="w-full mt-1 px-3 py-2 bg-secondary border border-border rounded-md text-sm"
                        placeholder="jan@example.com"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">{t.phone}</label>
                      <input
                        type="tel"
                        value={buyerPhone}
                        onChange={e => setBuyerPhone(e.target.value)}
                        className="w-full mt-1 px-3 py-2 bg-secondary border border-border rounded-md text-sm"
                        placeholder="+48 123 456 789"
                      />
                    </div>
                  </div>
                ) : (
                  /* Cart items list */
                  <div className="space-y-3">
                    {cart.items.map(item => (
                      <div key={item.photo_id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30">
                        {item.thumbnail ? (
                          <img
                            src={item.thumbnail}
                            alt=""
                            className="w-14 h-14 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded bg-secondary flex items-center justify-center flex-shrink-0">
                            <Camera className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono truncate">{item.filename || item.photo_id.slice(0, 12)}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-sm font-medium">{item.price} PLN</span>
                            {item.type === 'dog_pack' && (
                              <span className="text-xs bg-green-500/10 text-green-500 px-1.5 py-0.5 rounded">
                                {t.dogPack}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive flex-shrink-0"
                          onClick={() => handleRemoveFromCart(item.photo_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cart footer */}
              {cart.items.length > 0 && (
                <div className="border-t border-border px-6 py-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{t.total}</span>
                    <span className="text-xl font-bold">{cart.total} {cart.currency}</span>
                  </div>
                  {checkoutOpen ? (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setCheckoutOpen(false)}
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" /> {t.cart}
                      </Button>
                      <Button
                        className="flex-1"
                        disabled={!buyerName || !buyerEmail || checkoutLoading}
                        onClick={handleCheckout}
                      >
                        {checkoutLoading ? (
                          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {t.ordering}</>
                        ) : (
                          <><CreditCard className="w-4 h-4 mr-1" /> {t.pay}</>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleClearCart}>
                        <Trash2 className="w-4 h-4 mr-1" /> {t.clearCart}
                      </Button>
                      <Button className="flex-1" onClick={() => setCheckoutOpen(true)}>
                        <CreditCard className="w-4 h-4 mr-1" /> {t.checkout}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
